import { Alert, AlertText } from "@repo/components/src/ui/alert";
import {
  Avatar,
  AvatarFallbackText,
  AvatarImage,
} from "@repo/components/src/ui/avatar";
import { Badge, BadgeIcon, BadgeText } from "@repo/components/src/ui/badge";
import { Box } from "@repo/components/src/ui/box";
import { Button, ButtonText } from "@repo/components/src/ui/button";
import { Card } from "@repo/components/src/ui/card";
import { Divider } from "@repo/components/src/ui/divider";
import { Heading } from "@repo/components/src/ui/heading";
import { HStack } from "@repo/components/src/ui/hstack";
import { Link } from "@repo/components/src/ui/link";
import { Text } from "@repo/components/src/ui/text";
import {
  Tooltip,
  TooltipContent,
  TooltipText,
} from "@repo/components/src/ui/tooltip";
import { VStack } from "@repo/components/src/ui/vstack";
import {
  formatNumber,
  formatSeparators,
} from "@repo/components/src/utils/numbers";
import {
  instagramLink,
  tikTokLink,
} from "@repo/components/src/utils/socialNetworkUser";
import { PlatformCodeEnum } from "@repo/graphql/gql/graphql";
import { isCreatorSanctioned } from "@/utils/creatorSanction";
import {
  ArrowUpRight,
  Eye,
  Info,
  Sparkles,
  StarIcon,
} from "lucide-react-native";
import React, { useMemo, useState } from "react";
import { DimensionValue } from "react-native";
import { ProfileBodyProps } from "../../types";
import { getLocalityName } from "@repo/components/src/utils/area";

const getMatchBadgeAction = (
  matchPercentage: number,
): "success" | "warning" | "error" => {
  if (matchPercentage >= 75) return "success";
  if (matchPercentage >= 50) return "warning";
  return "error";
};

const getMatchIconColor = (matchPercentage: number): string => {
  if (matchPercentage >= 75) return "#16a34a";
  if (matchPercentage >= 50) return "#ca8a04";
  return "#dc2626";
};

const GeneralData = React.memo((props: ProfileBodyProps) => {
  const user = props.user;
  const isSanctioned = isCreatorSanctioned(user.creatorSanction?.status);
  const reportsCount = user.receivedReportsCount;
  const [expanded, setExpanded] = useState(false);
  const performance = props.user.userperformance;
  const avatarFile = user.profilePicture;
  const gender = props.user.gender;
  const firstAddress = props.user.useraddressSet.find(() => true)?.address;
  const address = firstAddress;
  const instagramSet = props.user.instagramSet;
  const tiktokSet = props.user.tiktokSet;
  const portfolios = props.user.userportfolioSet;

  const primarySocialAccount = instagramSet[0] ?? tiktokSet[0] ?? null;

  const HIDDEN_CATEGORIES = new Set([
    "Exp. con marcas",
    "Plataforma",
    "Presupuesto",
  ]);
  const topCategories = useMemo(() => {
    if (!props.scoreData) return [];
    const bd = props.scoreData.scoreBreakdown;
    return bd.categories
      .map((c) => c)
      .filter((c) => !HIDDEN_CATEGORIES.has(c.label));
  }, [props.scoreData]);

  const personalData: {
    title: string;
    value: string;
    width: DimensionValue;
  }[] = [
    {
      title: "País",
      value: address?.area?.country.name ?? "-",
      width: "50%",
    },
    { title: "Comuna", value: getLocalityName(address) ?? "-", width: "50%" },
    { title: "Género", value: gender?.name ?? "-", width: "50%" },
    { title: "Edad", value: user.age ? `${user.age} años` : "-", width: "50%" },
    { title: "Mail", value: user.email ?? "-", width: "100%" },
  ];
  return (
    <VStack space="md" className="w-80">
      <Card variant="outline" className="rounded-xl" size="lg">
        <Avatar size="2xl" className="border-4 border-background-50 mx-auto">
          <AvatarFallbackText>{user.fullName}</AvatarFallbackText>
          <AvatarImage source={{ uri: avatarFile?.url }} />
        </Avatar>
        <Heading className="text-center">{user.fullName}</Heading>
        <HStack
          space="sm"
          className="mx-auto w-fit mt-2 flex-wrap justify-center"
        >
          <Badge action="warning" className="items-start gap-x-1" rounded="lg">
            <BadgeText>
              {formatNumber(Number(performance?.avgReview ?? 0))} de 5
            </BadgeText>
            <BadgeIcon as={StarIcon} />
          </Badge>
          {!isSanctioned && reportsCount > 0 && (
            <Badge action="error" className="items-start gap-x-1" rounded="lg">
              <BadgeText>
                {reportsCount === 1
                  ? "1 reporte por incumplimiento"
                  : `${reportsCount} reportes por incumplimiento`}
              </BadgeText>
            </Badge>
          )}
          {props.scoreData && (
            <Tooltip
              placement="top"
              trigger={(tProps) => (
                <Badge
                  action={getMatchBadgeAction(props.scoreData!.matchPercentage)}
                  className="items-start gap-x-1 cursor-pointer"
                  rounded="lg"
                  variant="solid"
                  {...tProps}
                >
                  <BadgeText>
                    {props.scoreData!.matchPercentage}% match
                  </BadgeText>
                  <Info
                    size={12}
                    color={getMatchIconColor(props.scoreData!.matchPercentage)}
                  />
                </Badge>
              )}
            >
              <TooltipContent className="w-[280px] p-4 bg-typography-900 rounded-xl">
                <VStack space="md">
                  <HStack space="xs" className="items-center">
                    <Sparkles size={14} color="#fff" />
                    <TooltipText bold>Compatibilidad</TooltipText>
                  </HStack>
                  <VStack space="sm">
                    <HStack className="justify-between items-center">
                      <TooltipText size="xs" className="text-typography-400">
                        Match IA
                      </TooltipText>
                      <TooltipText bold className="text-success-400">
                        {props.scoreData.matchPercentage}%
                      </TooltipText>
                    </HStack>
                    {topCategories.map((cat) => (
                      <HStack
                        key={cat.label}
                        className="justify-between items-center"
                      >
                        <TooltipText size="xs" className="text-typography-400">
                          {cat.label}
                        </TooltipText>
                        <TooltipText bold>{cat.value}%</TooltipText>
                      </HStack>
                    ))}
                  </VStack>
                </VStack>
              </TooltipContent>
            </Tooltip>
          )}
        </HStack>

        {/* Suspension banner */}
        {isSanctioned && (
          <Alert action="error" className="mt-4">
            <AlertText size="sm">
              Este creador está suspendido por incumplimientos.
            </AlertText>
          </Alert>
        )}

        {/* AI Compatibility Alert */}
        {props.scoreData?.explanation && (
          <Alert action="muted" className="mt-4">
            <VStack space="xs" className="flex-1">
              <HStack space="xs" className="items-center">
                <Sparkles size={12} className="text-typography-500" />
                <AlertText bold>Insight IA</AlertText>
              </HStack>
              <AlertText size="sm">{props.scoreData.explanation}</AlertText>
            </VStack>
          </Alert>
        )}

        <Text className="mt-4" numberOfLines={!expanded ? 4 : undefined}>
          {user.biography}
        </Text>
        <Button
          onPress={() => setExpanded(!expanded)}
          variant="link"
          action="brand"
          className="w-fit"
          size="sm"
        >
          <ButtonText>{expanded ? "Ver menos" : "Ver más"}</ButtonText>
        </Button>
        <VStack className="mt-6">
          {[...instagramSet, ...tiktokSet].map((socialNetworkUser) => {
            let link = undefined;
            switch (socialNetworkUser.platform.enumCode) {
              case PlatformCodeEnum.Instagram:
                link = instagramLink(socialNetworkUser.username);
                break;
              case PlatformCodeEnum.TikTok:
                link = tikTokLink(socialNetworkUser.username);
                break;
            }
            return (
              <Link
                href={link}
                isExternal
                key={socialNetworkUser.id}
                className="w-full flex-row items-center p-2 rounded-lg data-[hover=true]:bg-background-100"
              >
                <Text className="flex-1">
                  {socialNetworkUser.platform.name}
                </Text>
                <Badge
                  action="muted"
                  size="md"
                  variant="solid"
                  rounded="lg"
                  className="border gap-x-1"
                  key={socialNetworkUser.id}
                >
                  <BadgeText>
                    {formatSeparators(socialNetworkUser.followers)}
                  </BadgeText>
                  <BadgeIcon as={ArrowUpRight} />
                </Badge>
              </Link>
            );
          })}
        </VStack>
        <Divider
          orientation="horizontal"
          className="my-1"
          hidden={
            portfolios.length === 0 ||
            (instagramSet.length === 0 && tiktokSet.length === 0)
          }
        />
        <VStack>
          {portfolios.map((portfolio) => {
            return (
              <Link
                href={portfolio.link}
                isExternal
                key={portfolio.id}
                className="w-full flex-row items-center p-2 rounded-lg data-[hover=true]:bg-background-100"
              >
                <Text className="flex-1">Portafolio</Text>
                <Badge
                  action="muted"
                  size="md"
                  variant="solid"
                  rounded="lg"
                  className="border gap-x-1"
                  key={portfolio.id}
                >
                  <BadgeText>Ver</BadgeText>
                  <BadgeIcon as={Eye} />
                </Badge>
              </Link>
            );
          })}
        </VStack>
      </Card>
      <Card variant="outline" className="rounded-xl" size="lg">
        <Heading bold>Datos personales</Heading>
        <HStack flexSpace="md" className="mt-3">
          {personalData.map((d) => {
            return (
              <Box key={d.title} style={{ width: d.width }}>
                <Heading className="text-typography-950" size="sm">
                  {d.title}
                </Heading>
                <Text size="sm" action="muted">
                  {d.value}
                </Text>
              </Box>
            );
          })}
        </HStack>
      </Card>
    </VStack>
  );
});

GeneralData.displayName = "GeneralData";

export default GeneralData;
